import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-detail-section>` says about itself.
 *
 * ── IT IS A CONTAINER, SO ITS EXAMPLES HOLD REAL THINGS ──────────────────────
 * An empty box tells a reader nothing about a piece whose whole job is holding fields, so every
 * example below carries real rows as nested placements. That is also the honest way to show what
 * the section brings and what it does not: the card, the heading and the padding are its; the
 * layout INSIDE it belongs to whatever is placed there.
 *
 * ── `read-only` IS A HYPHENATED ATTRIBUTE WITH NO PROPERTY MIRROR ────────────
 * There is no `el.readOnly` on the class — the attribute is the only spelling that sets it, so it
 * is declared under the hyphenated name rather than a camelCase one that names nothing.
 *
 * ── `collapsed` IS BOTH AN INPUT AND A REPORT ────────────────────────────────
 * An author sets it to decide how the section starts; after that the component sets and clears it
 * itself as the header is pressed, so reading it back tells you the current state.
 */
const docs: ComponentDocs = {
  aliases: ['section', 'panel', 'field group', 'record section', 'accordion', 'detail card', 'fieldset'],

  answers: [
    'group the fields on a record page under a heading',
    'let somebody fold away part of a long record page',
    'mark a group of fields as not editable',
    'put an Edit control beside a section heading',
  ],

  placements: ['recordPage', 'appPage'],

  inputs: [
    {
      name: 'heading',
      attribute: 'heading',
      type: 'text',
      description:
        'What this group of fields is called. Drawn as a mono uppercase category header over a ' +
        'hairline rule; a long one is truncated rather than wrapped.',
    },
    {
      name: 'collapsible',
      attribute: 'collapsible',
      type: 'boolean',
      description:
        'Turn the header into a real button that folds the content away, with a chevron that turns. ' +
        'Without it the header is a plain heading and nothing about the section is interactive.',
      default: false,
    },
    {
      name: 'collapsed',
      attribute: 'collapsed',
      type: 'boolean',
      description:
        'Start folded — and afterwards, the current state: the component sets and clears this itself ' +
        'as the header is pressed. Only meaningful alongside collapsible.',
      default: false,
    },
    {
      name: 'readOnly',
      attribute: 'read-only',
      type: 'boolean',
      description:
        'Draw the lock marker in the header saying these fields cannot be edited. It is a STATEMENT, ' +
        'not an enforcement — it does not disable anything placed inside, so whatever is in there ' +
        'has to be read-only in its own right.',
      default: false,
    },
  ],

  events: [
    {
      name: 'caos-section-toggle',
      when:
        'The header of a collapsible section is pressed, or toggle() is called — before the state ' +
        'changes. It is cancelable: preventing it leaves the section as it was, which is how a host ' +
        'refuses to let a section be folded.',
      carries: '{ collapsed } — the state it is about to move to.',
    },
  ],

  state: [],

  slots: [
    {
      name: '',
      takes:
        'The body of the section: the fields that belong together — KeyValueRow rows, FieldRenderer ' +
        'fields, or a table. The section supplies the padding around them and nothing else; how they ' +
        'are laid out is up to what is placed here.',
    },
    {
      name: 'actions',
      takes:
        'Controls that belong to this section, at the right-hand end of its header — an Edit button, ' +
        'an OverflowMenu. The row is not drawn when the slot is empty.',
    },
    {
      name: 'footer',
      takes:
        'What belongs UNDER the body rather than beside the heading: a total, a "3 of 12 shown" line, ' +
        'a last-updated stamp, or the buttons that act on the whole block. Divided from the body by ' +
        'the same hairline the header uses, so a section with both reads as one card. Not drawn when ' +
        'the slot is empty, and folded away with the body when the section is collapsed.',
    },
  ],

  tokens: [
    { token: '--caos-color-surface', controls: 'The ground of the card.' },
    { token: '--caos-color-border-strong', controls: 'The border around the card.' },
    { token: '--caos-radius-lg', controls: 'The corners of the card.' },
    { token: '--caos-shadow-card', controls: 'The elevation of the card.' },
    { token: '--caos-color-border', controls: 'The hairline rule under the header, and the one over the footer.' },
    { token: '--caos-color-surface-2', controls: 'The tint a collapsible header takes on hover.' },
    { token: '--caos-focus-ring', controls: 'The ring on a focused collapsible header.' },
    { token: '--caos-font-mono', controls: 'The face of the heading and of the read-only marker.' },
    { token: '--caos-color-text', controls: 'The heading ink.' },
    { token: '--caos-color-text-muted', controls: 'The chevron and the read-only marker.' },
    { token: '--caos-space-5', controls: 'The padding of the content, and the inset the header rule stops at.' },
    { token: '--caos-space-4', controls: 'The header padding down, and the footer’s.' },
    { token: '--caos-space-3', controls: 'The gap between the chevron, the heading and the actions.' },
    { token: '--caos-space-2', controls: 'The gap between the controls in the actions slot, and between whatever sits in the footer.' },
    { token: '--caos-space-1', controls: 'The gap between the lock glyph and the words "Read only".' },
  ],

  parts: [
    { part: 'section', is: 'the bordered section' },
    { part: 'header', is: 'the header strip' },
    {
      part: 'toggle',
      is: 'the header again — the same element, taking a second part name once it is a control',
      when: 'Only while the section is collapsible. A section that cannot fold carries nothing under this name, so a stylesheet written against it matches nothing.',
    },
    { part: 'title', is: 'the heading text' },
    { part: 'actions', is: 'the action group at the right of the header' },
    { part: 'content', is: 'the body the rows sit in' },
    {
      part: 'footer',
      is: 'the strip under the body',
      when: 'Always present as a part; it draws nothing until something is slotted into the footer.',
    },
  ],

  methods: [
    {
      name: 'toggle',
      description:
        'Fold or unfold the section, exactly as pressing the header does — the same cancelable event ' +
        'fires first. This is how something outside the section (an "expand all" control) drives it.',
      args: [],
    },
  ],

  placing:
    'A section is a block of a record page, stacked with the other sections down the page under the ' +
    'record header. It fills the width it is given and brings its own card — border, corners and ' +
    'elevation — so it wants a plain region around it rather than another panel. Sections are ' +
    'siblings, not nests: putting one inside another gives you two cards with two borders.',

  whenToUse:
    "Fields on a record that belong together and want naming — the record page's unit of structure. " +
    'Reach for it whenever a page has more fields than a person reads at once.',

  insteadUse: [
    {
      name: 'KeyValueRow',
      when: 'You want ONE pair, not a group. A single row does not need a titled card around it.',
    },
    {
      name: 'TabStrip',
      when:
        'The groups are alternatives rather than a stack — facets of the same record, one shown at a ' +
        'time, rather than sections read down the page.',
    },
    {
      name: 'PageHeader',
      when: 'The title belongs to the whole surface rather than to one block of it.',
    },
    {
      name: 'Inspector',
      when: 'The fields belong in a panel beside the record rather than in the page flow.',
    },
  ],

  accessibility:
    'A collapsible section is a real button carrying aria-expanded that tracks the state, so it is ' +
    'in the tab order, works from the keyboard and announces whether it is open — nothing to add. A ' +
    'non-collapsible section is a plain header with no button and no expanded state, which is ' +
    'correct: there is nothing to press. Two things are left to the author. The heading is styled ' +
    'text rather than an h2, so a surface that needs a heading outline has to provide one. And ' +
    'read-only draws a marker without enforcing anything — the fields inside must genuinely be ' +
    'read-only, or the marker is telling somebody something untrue.',

  examples: [
    {
      key: 'fields',
      label: 'A section of fields',
      summary:
        'The plain case: a titled card holding the rows that belong together, one of them with no ' +
        'value yet so the em dash shows.',
      placement: {
        component: 'caos-detail-section',
        inputs: { heading: 'Account information' },
        children: [
          { component: 'caos-key-value-row', inputs: { label: 'Name', value: 'Meridian Supply' } },
          { component: 'caos-key-value-row', inputs: { label: 'Owner', value: 'Dana Whitfield' } },
          { component: 'caos-key-value-row', inputs: { label: 'Region', value: 'North' } },
          { component: 'caos-key-value-row', inputs: { label: 'Reference', 'no-border': true } },
        ],
      },
    },
    {
      key: 'collapsible',
      label: 'Foldable, with an action',
      summary:
        'The same block made foldable — press the heading and it folds, and the chevron turns — with ' +
        'a control of its own in the header, which is where a section-scoped action goes.',
      placement: {
        component: 'caos-detail-section',
        inputs: { heading: 'Delivery', collapsible: true },
        children: [
          { component: 'caos-button', inputs: { slot: 'actions', variant: 'ghost', size: 'sm' }, children: ['Edit'] },
          { component: 'caos-key-value-row', inputs: { label: 'Region', value: 'North' } },
          { component: 'caos-key-value-row', inputs: { label: 'Terms', value: 'Standard' } },
          { component: 'caos-key-value-row', inputs: { label: 'Due date', value: '15 Sep 2026', 'no-border': true } },
        ],
      },
    },
    {
      key: 'read-only',
      label: 'Stated as read only',
      summary:
        'The lock marker in the header, for a group whose values come from somewhere else and are ' +
        'not edited here — a different statement from folding one away.',
      placement: {
        component: 'caos-detail-section',
        inputs: { heading: 'Amount summary', 'read-only': true },
        children: [
          { component: 'caos-key-value-row', inputs: { label: 'Subtotal', value: '12,400' } },
          { component: 'caos-key-value-row', inputs: { label: 'Adjustments', value: '1,150' } },
          { component: 'caos-key-value-row', inputs: { label: 'Total', value: '13,550', 'no-border': true } },
        ],
      },
    },
  ],
};

export default docs;
